/**
 * The van app's three calls. Setting the phone up uses the salesman's
 * sign-in once; after that the phone speaks for itself with its own token,
 * so it can sync from the road without anyone typing a password.
 */
import { api } from '../api/client'
import type { Bootstrap, VanDoc } from './model'

const VAN = '/api/v1/van'

export class VanHttpError extends Error {
  constructor(message: string, readonly status: number) { super(message) }
}

export async function registerPhone(body: { device_name: string; takeover?: boolean;
                                            store_id?: number }):
Promise<{ token: string; bootstrap: Bootstrap }> {
  const { data } = await api.post(`${VAN}/register`, body)
  return data
}

async function call<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${VAN}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', 'X-Van-Device': token,
                 ...(init.headers ?? {}) },
    })
  } catch {
    throw new VanHttpError('No connection', 0)
  }
  if (!res.ok) {
    let msg = res.statusText
    try { msg = (await res.json()).detail ?? msg } catch { /* not JSON */ }
    throw new VanHttpError(String(msg), res.status)
  }
  return res.json() as Promise<T>
}

export function fetchBootstrap(token: string): Promise<Bootstrap> {
  return call<Bootstrap>('/bootstrap', token)
}

export interface SyncReply {
  results: { uid: string; doc_no: string; status: 'POSTED' | 'DUPLICATE' | 'FAILED'
             warnings: string[]; error?: string | null; server_id?: number | null }[]
  server_time: string
  bootstrap?: Bootstrap
}

export function syncDocs(token: string, docs: VanDoc[], refresh = false): Promise<SyncReply> {
  // The local bookkeeping stays on the phone.
  const documents = docs.map(({ sync: _s, ...d }) => d)
  return call<SyncReply>('/sync', token, {
    method: 'POST', body: JSON.stringify({ documents, refresh }),
  })
}

export interface UnloadReply { doc_no: string; transfer_id: number; to_store: string
                               lines: number; bootstrap: Bootstrap }

export function unloadVan(token: string, body: { to_store_id?: number
  lines?: { item_code: string; qty: string }[]; notes?: string } = {}): Promise<UnloadReply> {
  return call<UnloadReply>('/unload', token, { method: 'POST', body: JSON.stringify(body) })
}
